import { locales, defaultLocale } from '~/config/locales'
import { I18N_REDIRECT_COOKIE_NAME } from '~/config/i18n'

// Guests reach an event through the short-link / QR redirector (mshr.io), which
// lands them on the UNPREFIXED, default-locale event URL (/event/<name>/<id>).
// Global browser-language detection is limited to '/' (i18n redirectOn: 'root',
// commit 056b4f5) for SEO, so these deep-links stopped following the visitor's
// browser language and always rendered English. Event pages are noindex +
// robots-disallowed, so a language redirect here costs nothing for SEO.
//
// Only the unprefixed `/event/...` form is matched; once redirected to
// `/<locale>/event/...` the guard no longer matches, so there is no loop.

// `locales` is a static import, so the supported-code set never changes — build
// it once at module scope rather than on every navigation.
const supported = new Set(locales.map((locale) => locale.code))

export default defineNuxtRouteMiddleware((to) => {
  if (!/^\/event(\/|$)/.test(to.path)) {
    return
  }

  // Honour a previous explicit choice (cookie) before the browser preference,
  // mirroring i18n's own detection precedence.
  const cookieLocale = useCookie(I18N_REDIRECT_COOKIE_NAME).value
  const preferredLocale = supported.has(cookieLocale) ? cookieLocale : detectBrowserLocale(supported)

  if (!preferredLocale || preferredLocale === defaultLocale) {
    return
  }

  const localePath = useNuxtApp().$localePath

  return navigateTo(localePath({ path: to.path, query: to.query }, preferredLocale), { redirectCode: 302 })
})

function detectBrowserLocale(supported) {
  const languages = import.meta.client
    ? navigator.languages?.length
      ? [...navigator.languages]
      : [navigator.language].filter(Boolean)
    : (useRequestHeaders(['accept-language'])['accept-language'] ?? '')
        .split(',')
        .map((part) => part.split(';')[0].trim())
        .filter(Boolean)

  for (const language of languages) {
    const code = language.toLowerCase().split('-')[0]

    if (supported.has(code)) {
      return code
    }
  }

  return null
}
